/**
 * Sentry SDK initialization for crash reporting and field logging.
 * Called from main.jsx before ReactDOM.createRoot().
 *
 * Handles JS errors, native crashes (via @sentry/capacitor),
 * breadcrumbs, and session replay on error.
 */

import * as SentryReact from '@sentry/react'

let initialized = false

export function initSentry() {
  if (initialized) return
  initialized = true

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    console.warn('[sentry] No VITE_SENTRY_DSN — Sentry disabled')
    return
  }

  // Detect environment from Capacitor platform
  let environment = 'web'
  if (window.Capacitor?.isNativePlatform?.()) {
    const platform = window.Capacitor.getPlatform()
    environment = platform === 'ios' ? 'native-ios' : platform === 'android' ? 'native-android' : 'native'
  }

  SentryReact.init({
    dsn,
    environment,
    release: `press@${__APP_VERSION__}`,

    // 10% performance sampling — lightweight baseline
    tracesSampleRate: 0.1,

    // Capture replay on 50% of errors (the "golf course debugger")
    replaysOnErrorSampleRate: 0.5,
    // No passive replay — saves bandwidth on cellular
    replaysSessionSampleRate: 0,

    integrations: [
      SentryReact.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    maxBreadcrumbs: 50,

    beforeSend(event) {
      // Scrub PII — keep only Supabase user ID
      if (event.user) {
        delete event.user.email
        delete event.user.username
        delete event.user.ip_address
      }

      // Attach score queue stats as extra context
      if (typeof window !== 'undefined' && window.__scoreQueue?.stats) {
        try {
          event.extra = { ...event.extra, scoreQueueStats: window.__scoreQueue.stats() }
        } catch { /* ignore */ }
      }

      // Tag events that fire within 10 s of an app resume so we can filter
      // and group resume-related issues in Sentry. Read from window.__lifecycle
      // populated by src/lib/lifecycle.js.
      try {
        const lc = typeof window !== 'undefined' ? window.__lifecycle : null
        if (lc?.lastResumeAt) {
          const elapsed = Date.now() - lc.lastResumeAt
          if (elapsed >= 0 && elapsed <= 10_000) {
            event.tags = { ...event.tags, resumeRelated: 'true', msSinceResume: String(elapsed) }
          }
        }
      } catch { /* ignore */ }

      return event
    },

    beforeBreadcrumb(breadcrumb) {
      // Filter noisy Supabase realtime heartbeat XHRs
      if (
        breadcrumb.category === 'xhr' &&
        breadcrumb.data?.url?.includes('/realtime/')
      ) {
        return null
      }
      return breadcrumb
    },
  })
}

export { SentryReact as Sentry }
